import { isKeyOf } from "../../../../types/internal/utility";
import {
    getTempOption,
    hasTempOption,
    type OptionValue,
    setTempOption,
} from "../Options/Options";
import Request from "../Request";

type EnterprisePolicyProductSource = "all" | "enterprise" | "enterprise_instance" | "enterprises_with_api" | "api";

export interface ShopfrontEnterprisePolicies {
    enterprise_login_visible: boolean;
    create_products_source: EnterprisePolicyProductSource;
    update_products_source: EnterprisePolicyProductSource;
    delete_products_source: EnterprisePolicyProductSource;
    create_classifications_source: EnterprisePolicyProductSource;
    update_classifications_source: EnterprisePolicyProductSource;
    delete_classifications_source: EnterprisePolicyProductSource;
    create_suppliers_source: EnterprisePolicyProductSource;
    update_suppliers_source: EnterprisePolicyProductSource;
    delete_suppliers_source: EnterprisePolicyProductSource;
    create_tax_rates_source: EnterprisePolicyProductSource;
    update_tax_rates_source: EnterprisePolicyProductSource;
    delete_tax_rates_source: EnterprisePolicyProductSource;
    gift_card_sources: Array<{
        connection: string;
        name: string;
        address: string;
        expiry: string;
    }>;
    order_cost_adjustments: boolean;
}

export const getPolicy = <TKey extends keyof ShopfrontEnterprisePolicies>(
    name: TKey,
    defaultValue: ShopfrontEnterprisePolicies[TKey]
): ShopfrontEnterprisePolicies[TKey] => {
    return getTempOption(`policy_${name}`, defaultValue) as ShopfrontEnterprisePolicies[TKey];
};

export const arePoliciesLoaded = (): boolean => {
    return hasTempOption("policy_enterprise_login_visible");
};

export const loadEnterprisePolicies = async (): Promise<void> => {
    const response = await Request.get<Record<string, OptionValue>>("/enterprise/policies");

    if(response.status !== 200) {
        throw new Error("Invalid response provided when loading enterprise policies");
    }

    storeEnterprisePolicies(response.data);
};

export const storeEnterprisePolicies = (values: Record<string, unknown>): void => {
    for(const key in values) {
        if(!isKeyOf(values, key)) {
            continue;
        }

        setTempOption(`policy_${key}`, values[key] as any);
    }
};
